import { useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    Box,
    Stack,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Heading,
    Alert,
    AlertIcon,
    AlertDescription,
} from '@chakra-ui/react';
import { Button } from '@/components/v4';
import { Card } from '@/components/v4';
import {
    breadCrumbStyling,
    headingStyling,
} from 'components/forms/new/formStyles';
import { toast } from 'react-toastify';
import { useQuery } from 'hooks/useCustomQuery';
import useGetCompanyServices from '@/queries/appAuth/useGetCompanyServices';
import {
    createPlatformUser,
    getSites,
} from 'pages/services/eightByEight/userManagement/api';

// import StepperHeader from './components/StepperHeader';
import CreateUserStep from './steps/CreateUserStep';
// import LicencesStep from './steps/LicencesStep';
// import PhoneNumbersStep from './steps/PhoneNumbersStep';

const STEPS = ['Create User']; // , 'Licences', 'Phone Numbers'];

const initialValues = {
    accountType: 'User',
    firstName: '',
    lastName: '',
    name: '',
    email: '',
    siteId: '',
    platformExtension: '',
};

const createUserValidationSchema = Yup.object({
    accountType: Yup.string().oneOf(['User', 'RingGroup']).required(),
    firstName: Yup.string().when('accountType', {
        is: (accountType) => accountType !== 'RingGroup',
        then: (schema) => schema.required('First Name is required'),
    }),
    lastName: Yup.string().when('accountType', {
        is: (accountType) => accountType !== 'RingGroup',
        then: (schema) => schema.required('Last Name is required'),
    }),
    name: Yup.string().when('accountType', {
        is: 'RingGroup',
        then: (schema) => schema.required('Name is required'),
    }),
    // Email is optional for every account type — only users with 8x8
    // permission can even see this form, so a stricter check isn't needed.
    email: Yup.string().email('Invalid email address'),
    siteId: Yup.string().required('Site is required'),
    platformExtension: Yup.string().matches(
        /^\d*$/,
        'Platform Extension must contain only numbers',
    ),
});

export default function AddUser() {
    const [currentStep, setCurrentStep] = useState(0);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { darkMode } = useSelector((state) => state.settings);
    const { currentCompany } = useSelector((state) => state.navigationLists);
    const { requiredScope } = useSelector((state) => state.login);

    const { data: companyServices = [] } =
        useGetCompanyServices(currentCompany);
    const eightByEightService = companyServices.find(
        (s) => s.serviceType === 'EightByEight',
    );
    const serviceId = eightByEightService?.id;
    const platformId = eightByEightService?.platformID;

    const { data: sites = [] } = useQuery({
        queryKey: ['8x8SitesForAddUser', serviceId],
        queryFn: () => getSites(serviceId, requiredScope).then((r) => r.data),
        enabled: !!serviceId,
        staleTime: 0,
        cacheTime: 0,
    });

    const siteOptions = sites.map((s) => ({
        value: s.id,
        label: s.name || s.id,
    }));

    const isLastStep = currentStep === STEPS.length - 1;

    const handleNext = () => setCurrentStep((s) => s + 1);

    const handleBack = () => {
        if (currentStep === 0) {
            navigate('/users');
        } else {
            setCurrentStep((s) => s - 1);
        }
    };

    const handleSubmit = async (values, actions) => {
        setError(null);
        const isRingGroup = values.accountType === 'RingGroup';
        try {
            await createPlatformUser(
                isRingGroup
                    ? {
                          platformID: platformId,
                          companyID: currentCompany,
                          siteID: values.siteId || null,
                          givenName: '',
                          surname: '',
                          email: values.email,
                          displayName: values.name.trim(),
                          userTypeID: 'RingGroup',
                          platformExtension: values.platformExtension,
                      }
                    : {
                          platformID: platformId,
                          companyID: currentCompany,
                          siteID: values.siteId || null,
                          givenName: values.firstName,
                          surname: values.lastName,
                          email: values.email,
                          displayName:
                              `${values.firstName} ${values.lastName}`.trim(),
                          userTypeID: 'User',
                          // platformAttribute1: null,
                          // platformAttribute2: null,
                          // platformAttribute3: null,
                      },
                requiredScope,
            );
            toast.success(
                isRingGroup
                    ? 'Ring Group created successfully'
                    : 'User created successfully',
            );
            navigate('/users');
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.message ||
                'An error occurred. Please try again.';
            setError(message);
        } finally {
            actions.setSubmitting(false);
        }
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={createUserValidationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={false}>
            {({
                values,
                setFieldValue,
                errors,
                touched,
                submitForm,
                isSubmitting,
            }) => (
                <Box>
                    <Breadcrumb sx={breadCrumbStyling}>
                        <BreadcrumbItem>
                            <BreadcrumbLink as={Link} to="/users">
                                Users
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbItem isCurrentPage>
                            <BreadcrumbLink>Add User(s)</BreadcrumbLink>
                        </BreadcrumbItem>
                    </Breadcrumb>

                    <Stack>
                        <Heading sx={headingStyling}>Add User(s)</Heading>
                    </Stack>

                    {/* <StepperHeader steps={STEPS} currentStep={currentStep} /> */}

                    {error && (
                        <Alert status="error" mb={4} maxWidth={895}>
                            <AlertIcon />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    <form>
                        <Stack spacing={30}>
                            <Card
                                borderRadius={15}
                                padding="15px 30px"
                                maxWidth={895}
                                ml={0}
                                bg={darkMode ? 'dark.tcap' : null}>
                                <Stack sx={{ margin: '0 !important' }}>
                                    {currentStep === 0 && (
                                        <CreateUserStep
                                            values={values}
                                            setFieldValue={setFieldValue}
                                            errors={errors}
                                            touched={touched}
                                            darkMode={darkMode}
                                            siteOptions={siteOptions}
                                        />
                                    )}
                                    {/* {currentStep === 1 && (
                                        <LicencesStep
                                            values={values}
                                            setFieldValue={setFieldValue}
                                            darkMode={darkMode}
                                        />
                                    )}
                                    {currentStep === 2 && (
                                        <PhoneNumbersStep
                                            values={values}
                                            setFieldValue={setFieldValue}
                                            errors={errors}
                                            touched={touched}
                                            darkMode={darkMode}
                                        />
                                    )} */}
                                </Stack>
                            </Card>

                            {/* Buttons — matches FormBase exact layout */}
                            <Stack
                                direction="row"
                                maxWidth={895}
                                sx={{ paddingBottom: 10 }}>
                                <div
                                    className="edit-form-buttons"
                                    style={{
                                        border: 'none',
                                        textAlign: 'center',
                                        justifyContent: 'center',
                                    }}>
                                    <Button
                                        sx={{ width: 142 }}
                                        isLoading={isLastStep && isSubmitting}
                                        onClick={
                                            isLastStep ? submitForm : handleNext
                                        }>
                                        {isLastStep ? 'Create User' : 'Next'}
                                    </Button>
                                    <div
                                        style={{
                                            marginLeft: 30,
                                            display: 'inline',
                                        }}>
                                        <Button
                                            className="secondary h-10 min-w-[5rem] font-semibold"
                                            variant="outline"
                                            background="white"
                                            sx={{ width: 142 }}
                                            onClick={handleBack}>
                                            Back
                                        </Button>
                                    </div>
                                </div>
                            </Stack>
                        </Stack>
                    </form>
                </Box>
            )}
        </Formik>
    );
}
